import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from './AuthContext';
import { PasswordRequirements } from './PasswordRequirements';
import { authErrorMessage } from './util_authErrors';
import { checkPassword, MIN_PASSWORD_LENGTH } from './util_password';

type ModeType = 'sign-in' | 'create-account';

interface EmailPasswordFormProps {
  /** Which form to show first. The user can switch between the two. */
  mode: ModeType;
  /**
   * Locks the email field — used on the invitation page, where the account
   * must be created for the invited address and nothing else.
   */
  fixedEmail?: string;
  disabled?: boolean;
  onError: (message: string | null) => void;
}

const inputClass =
  'tw:w-full tw:rounded tw:border tw:border-white/20 tw:bg-white/5 tw:px-3 tw:py-2.5 tw:text-sm tw:text-white tw:placeholder-white/30 tw:outline-none tw:focus:border-ed-gold tw:disabled:opacity-60';

/**
 * Email + password sign-in / account creation, styled for the dark sign-in and
 * invitation screens. Google remains the primary path; this exists for partners
 * whose partner does not use Google accounts.
 *
 * The create-account half enforces the same rule as the manager registration
 * wizard's own credentials step (`OnboardingStepAccount`): a minimum length and
 * a live requirements checklist, so a staff member and a manager are held to one
 * password policy rather than two.
 */
export function EmailPasswordForm({ mode, fixedEmail, disabled, onError }: EmailPasswordFormProps) {
  const { signInWithEmail, createAccountWithEmail, sendPasswordReset } = useAuth();

  const [currentMode, setCurrentMode] = useState<ModeType>(mode);
  const [email, setEmail] = useState(fixedEmail ?? '');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Separate from `busy`: sending a reset email must not look like the sign-in
  // button is working, and vice versa.
  const [resetting, setResetting] = useState(false);

  const isCreate = currentMode === 'create-account';
  const effectiveEmail = fixedEmail ?? email;

  const checks = checkPassword(password, confirmPassword);
  // Sign-in has no requirements of its own to check here — Firebase is the
  // judge of whether an existing password is correct.
  const canSubmit = !isCreate || checks.ok;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (isCreate) {
        await createAccountWithEmail(effectiveEmail, password, displayName);
      } else {
        await signInWithEmail(effectiveEmail, password);
      }
    } catch (err) {
      console.error(err);
      onError(authErrorMessage(err));
      // The most common stumble: they already made an account on an earlier
      // visit. Put them straight on the sign-in form rather than making them
      // work out what to do.
      if ((err as { code?: string }).code === 'auth/email-already-in-use') {
        setCurrentMode('sign-in');
        setConfirmPassword('');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleReset() {
    if (!effectiveEmail) {
      onError('Enter your email address first.');
      return;
    }
    onError(null);
    setResetting(true);
    try {
      await sendPasswordReset(effectiveEmail);
      setNotice(`Password reset email sent to ${effectiveEmail}.`);
    } catch (err) {
      console.error(err);
      onError(authErrorMessage(err));
    } finally {
      setResetting(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className='tw:mt-6 tw:space-y-3 tw:text-left'>
      <input
        type='email'
        value={effectiveEmail}
        readOnly={!!fixedEmail}
        required
        placeholder='you@example.com'
        autoComplete='email'
        onChange={(e) => setEmail(e.target.value)}
        className={inputClass}
      />

      {isCreate && (
        <input
          type='text'
          value={displayName}
          placeholder='Your name'
          autoComplete='name'
          onChange={(e) => setDisplayName(e.target.value)}
          className={inputClass}
        />
      )}

      <input
        type='password'
        value={password}
        required
        minLength={isCreate ? MIN_PASSWORD_LENGTH : undefined}
        placeholder={isCreate ? `Min ${MIN_PASSWORD_LENGTH} characters` : 'Password'}
        autoComplete={isCreate ? 'new-password' : 'current-password'}
        onChange={(e) => setPassword(e.target.value)}
        className={inputClass}
      />

      {isCreate && (
        <input
          type='password'
          value={confirmPassword}
          required
          placeholder='Re-enter password'
          autoComplete='new-password'
          onChange={(e) => setConfirmPassword(e.target.value)}
          className={inputClass}
        />
      )}

      {isCreate && <PasswordRequirements checks={checks} />}

      <button
        type='submit'
        disabled={busy || disabled || !canSubmit}
        aria-busy={busy || undefined}
        className='tw:inline-flex tw:w-full tw:items-center tw:justify-center tw:gap-2.5 tw:rounded tw:bg-ed-gold tw:px-6 tw:py-2.5 tw:text-sm tw:font-medium tw:text-ed-black tw:transition-opacity tw:hover:opacity-90 tw:disabled:opacity-60'
      >
        {busy && <Loader2 className='tw:size-4 tw:animate-spin' />}
        {busy ? 'Please wait…' : isCreate ? 'Create account' : 'Sign in'}
      </button>

      <div className='tw:flex tw:justify-between tw:text-xs tw:text-white/50'>
        <button
          type='button'
          onClick={() => {
            setCurrentMode(isCreate ? 'sign-in' : 'create-account');
            setConfirmPassword('');
          }}
          className='tw:underline tw:underline-offset-4 tw:transition-colors tw:hover:text-ed-gold'
        >
          {isCreate ? 'I already have a password' : 'Set up a password'}
        </button>
        {!isCreate && (
          <button
            type='button'
            onClick={() => void handleReset()}
            disabled={resetting}
            aria-busy={resetting || undefined}
            className='tw:inline-flex tw:items-center tw:gap-1.5 tw:underline tw:underline-offset-4 tw:transition-colors tw:hover:text-ed-gold'
          >
            {resetting && <Loader2 className='tw:size-3 tw:animate-spin' />}
            Forgot password?
          </button>
        )}
      </div>

      {notice && <p className='tw:text-xs tw:text-ed-gold'>{notice}</p>}
    </form>
  );
}
